import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../services/api";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import { PageLayout } from "../components/layout/Navbar";
import { Card, Badge } from "../components/ui";
import { useModal } from "../context/ModalContext";
import { Link } from "react-router-dom";

const SettingsOption = ({ icon, title, description, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
  >
    <Card className="p-8 bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl shadow-slate-900/5 group hover:border-[#1e9f6b]/30 transition-all duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-8">
        <div className="flex items-start gap-6">
          <div className="h-16 w-16 bg-slate-50 dark:bg-slate-800 rounded-[24px] flex items-center justify-center text-slate-400 group-hover:text-[#1e9f6b] group-hover:bg-[#1e9f6b]/10 transition-all duration-500 shadow-inner">
            <span className="material-symbols-outlined text-[32px]">{icon}</span>
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-black text-slate-900 dark:text-white italic uppercase tracking-tight group-hover:text-[#1e9f6b] transition-colors">{title}</h3>
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest max-w-[280px] leading-relaxed">{description}</p>
          </div>
        </div>
        <div className="flex-shrink-0 w-full sm:w-auto">
          {children}
        </div>
      </div>
    </Card>
  </motion.div>
);

function SettingsPage() {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const { openModal } = useModal();
  const [sessions, setSessions] = React.useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = React.useState(false);
  const [confirmModal, setConfirmModal] = React.useState(null); // { type: 'single'|'others', sessionId?, device? }
  const [isTerminating, setIsTerminating] = React.useState(false);

  const fetchSessions = React.useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const data = await authApi.getSessions();
      setSessions(data || []);
    } catch (err) {
      console.error("Failed to fetch sessions", err);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  React.useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const terminateSession = async (id) => {
    setIsTerminating(true);
    try {
      await authApi.terminateSession(id);
      toast.success(t("settings.sessionTerminated") || "Sessiya tugatildi");
      setConfirmModal(null);
      fetchSessions();
    } catch (err) {
      toast.error(err.message || "Xatolik yuz berdi");
    } finally {
      setIsTerminating(false);
    }
  };

  const terminateOtherSessions = async () => {
    setIsTerminating(true);
    try {
      const { getRefreshToken } = await import('../services/tokenStorage');
      await authApi.terminateOtherSessions(getRefreshToken());
      toast.success(t("settings.otherSessionsTerminated") || "Boshqa barcha sessiyalar tugatildi");
      setConfirmModal(null);
      fetchSessions();
    } catch (err) {
      toast.error(err.message || "Xatolik yuz berdi");
    } finally {
      setIsTerminating(false);
    }
  };

  const handleConfirm = () => {
    if (!confirmModal) return;
    if (confirmModal.type === 'single') terminateSession(confirmModal.sessionId);
    else terminateOtherSessions();
  };

  const currentLanguage = i18n.language === "ru" || i18n.language === "uz_latin" ? i18n.language : "uz_cyrl";

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto py-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-2 w-12 bg-[#1e9f6b] rounded-full" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-[#1e9f6b] italic">CONFIG</span>
          </div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-11 w-11 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-slate-600 dark:text-slate-400 text-[24px]" style={{ fontVariationSettings: "'FILL' 1" }}>settings</span>
            </div>
            <h1 className="text-4xl font-black text-slate-900 dark:text-white italic uppercase tracking-tighter">{t("settings.title") || "Sozlamalar"}</h1>
          </div>
          <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-2 opacity-70">
            {t("settings.subtitle") || "Profil va ilova ko'rinishini boshqaring"}
          </p>
        </motion.div>

        <div className="space-y-6">
          {/* Language Selection */}
          <SettingsOption 
            icon="language" 
            title={t("settings.language")} 
            description={t("settings.languageDesc") || "Ilova tilini o'zingizga qulay formatga o'zgartiring"}
            delay={0.1}
          >
            <div className="relative group/select min-w-[200px]">
              <select
                value={currentLanguage}
                onChange={(e) => {
                  i18n.changeLanguage(e.target.value);
                  localStorage.setItem("app_language", e.target.value);
                }}
                className="w-full h-14 bg-slate-50 dark:bg-slate-800 border-2 border-transparent hover:border-[#1e9f6b]/30 dark:text-white rounded-2xl px-6 outline-none transition-all appearance-none font-black text-[11px] uppercase tracking-widest cursor-pointer shadow-inner"
              >
                <option value="uz_latin">{t("settings.languages.uzLatin")}</option>
                <option value="uz_cyrl">{t("settings.languages.uzCyrl")}</option>
                <option value="ru">{t("settings.languages.ru")}</option>
              </select>
              <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover/select:text-[#1e9f6b] transition-colors">expand_more</span>
            </div>
          </SettingsOption>

          {/* Account Security Preview */}
          <SettingsOption 
            icon="security" 
            title={t("security.title")} 
            description={t("security.subtitle") || "Parol va xavfsizlik sozlamalarini boshqarish"}
            delay={0.3}
          >
            <button
              type="button"
              onClick={() => openModal('security')}
              className="h-14 px-8 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 transition-all active:scale-95 shadow-xl shadow-slate-900/10"
            >
              {t("common.edit") || "O'zgartirish"}
            </button>
          </SettingsOption>

          {/* Premium Status */}
          <SettingsOption 
            icon="workspace_premium" 
            title={t("premiumPanel.premium")}
            description={t("premium.subtitle") || "Barcha imkoniyatlardan cheksiz foydalaning"}
            delay={0.4}
          >
            {user?.isPremium ? (
              <Badge variant="amber" pulse className="h-12 px-6 rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] italic border-none bg-amber-400 text-white">
                {t("premium.active") || "FAOL"}
              </Badge>
            ) : (
              <Link
                to="/premium"
                className="inline-flex h-14 px-8 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 transition-all active:scale-95 shadow-xl shadow-amber-500/20 items-center justify-center"
              >
                {t("premium.get") || "PREMIUM OLISH"}
              </Link>
            )}
          </SettingsOption>

          {/* Active Sessions (Telegram Style) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12"
          >
            <div className="flex items-center justify-between mb-6 px-4">
              <div className="space-y-1">
                <h2 className="text-xl font-black text-slate-900 dark:text-white italic uppercase tracking-tighter">
                  {t("settings.activeSessions") || "Faol sessiyalar"}
                </h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-relaxed">
                  {t("settings.sessionsDesc") || "Hisobingizga ulangan barcha qurilmalar"}
                </p>
              </div>
              {sessions.length > 1 && (
                <button 
                  onClick={() => setConfirmModal({ type: 'others' })}
                  className="text-[10px] font-black text-rose-500 hover:text-rose-600 uppercase tracking-widest transition-colors p-2 hover:bg-rose-500/5 rounded-xl"
                >
                  {t("settings.terminateOther") || "Barchasini tugatish"}
                </button>
              )}
            </div>

            <Card className="overflow-hidden bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-xl shadow-slate-900/5">
              <div className="divide-y divide-slate-50 dark:divide-slate-800/50">
                {isLoadingSessions ? (
                  <div className="p-12 flex justify-center">
                    <div className="h-8 w-8 border-4 border-[#1e9f6b] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 font-black uppercase text-[10px] tracking-widest italic opacity-50">
                    {t("settings.noSessions")}
                  </div>
                ) : (
                  sessions.map((session, idx) => {
                    const isCurrent = session.isCurrent === true || idx === 0;
                    return (
                      <div key={session.id} className={`p-8 flex items-center justify-between group transition-colors ${isCurrent ? 'bg-[#1e9f6b]/5 border-l-4 border-[#1e9f6b]' : 'hover:bg-slate-50/50 dark:hover:bg-slate-800/20'}`}>
                        <div className="flex items-center gap-6">
                          <div className={`h-14 w-14 rounded-[20px] flex items-center justify-center transition-all duration-500 ${
                            isCurrent ? "bg-[#1e9f6b]/10 text-[#1e9f6b]" : "bg-slate-50 dark:bg-slate-800 text-slate-400"
                          }`}>
                            <span className="material-symbols-outlined text-[28px]">
                              {session.device?.toLowerCase().includes("mobi") ? "smartphone" : "desktop_windows"}
                            </span>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-3">
                              <h4 className="text-sm font-black text-slate-900 dark:text-white italic uppercase tracking-tight">
                                {session.device || t("settings.unknownDevice")}
                              </h4>
                              {isCurrent && (
                                <Badge variant="success" className="text-[8px] px-2 py-0.5 rounded-lg border-none bg-[#1e9f6b]/10 text-[#1e9f6b]">
                                  {t("settings.current")}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                              <span>{session.ip}</span>
                              <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
                              <span>{new Date(session.lastSeen).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        {!isCurrent && (
                          <button 
                            onClick={() => setConfirmModal({ 
                              type: 'single', 
                              sessionId: session.id, 
                              device: session.device 
                            })}
                            className="h-12 w-12 flex items-center justify-center text-slate-300 hover:text-rose-500 hover:bg-rose-500/10 rounded-2xl transition-all duration-500"
                            title={t("settings.terminateSession")}
                          >
                            <span className="material-symbols-outlined">logout</span>
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Info Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16 flex flex-col items-center gap-6 text-center"
        >
          <div className="h-10 w-10 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-center grayscale opacity-50">
            <span className="material-symbols-outlined text-[20px]">verified_user</span>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">SmartGaz v2.0.4-dev</p>
            <p className="text-[9px] font-bold text-slate-300 dark:text-slate-600 uppercase tracking-widest">© {new Date().getFullYear()} SmartGaz</p>
          </div>
        </motion.div>
      </div>

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isTerminating && setConfirmModal(null)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative bg-white dark:bg-slate-900 rounded-[32px] p-8 max-w-sm w-full shadow-2xl border border-slate-100 dark:border-slate-800"
          >
            <div className="text-center space-y-6">
              <div className="h-20 w-20 bg-rose-500/10 rounded-[28px] flex items-center justify-center mx-auto">
                <span className="material-symbols-outlined text-[40px] text-rose-500">logout</span>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">
                  {confirmModal.type === 'others' ? t("settings.confirmTerminateAll") : t("settings.confirmTerminateOne")}
                </h3>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider leading-relaxed">
                  {confirmModal.type === 'others'
                    ? t("settings.confirmTerminateAllDesc")
                    : confirmModal.device
                      ? t("settings.confirmTerminateDeviceDesc", { device: confirmModal.device.slice(0, 50) })
                      : t("settings.confirmTerminateOneDesc")
                  }
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal(null)}
                  disabled={isTerminating}
                  className="flex-1 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 transition-all disabled:opacity-50"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isTerminating}
                  className="flex-1 h-14 rounded-2xl bg-rose-500 text-white font-black uppercase text-[10px] tracking-widest hover:bg-rose-600 transition-all shadow-lg shadow-rose-500/30 disabled:opacity-50 flex items-center justify-center"
                >
                  {isTerminating
                    ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : t("settings.confirmYes")
                  }
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </PageLayout>
  );
}

export default SettingsPage;